/**
 * RejectOfferModal — reject an incoming offer with optional reason.
 *
 * Replaces the inline modal previously embedded in OfferManagement.js
 * (lines ~1696–1715). Uses ui/Modal for the shell.
 */
import React, { useEffect, useId, useState } from 'react';
import Modal from '../../ui/Modal';
import './RejectOfferModal.css';

const MAX_REASON = 500;
const ANNOUNCE_THRESHOLD = 50;

export default function RejectOfferModal({
  open,
  onClose,
  offer,
  currentRole, // eslint-disable-line no-unused-vars
  submitting = false,
  onSubmit,
  error,
}) {
  const [reason, setReason] = useState('');

  const promptId = useId();
  const reasonId = useId();
  const counterId = useId();
  const errorId = useId();

  useEffect(() => {
    if (open) setReason('');
  }, [open, offer?.id]);

  const remaining = MAX_REASON - reason.length;
  const showAnnounce = remaining < ANNOUNCE_THRESHOLD;

  const handleSubmit = () => {
    if (submitting || !offer) return;
    onSubmit?.({ offer, reason });
  };

  const footer = (
    <>
      {error && (
        <p id={errorId} role="alert" className="reject-modal__error">
          {error}
        </p>
      )}
      <div className="reject-modal__actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-danger"
          onClick={handleSubmit}
          disabled={submitting || !offer}
          aria-describedby={error ? errorId : undefined}
        >
          {submitting ? 'Rejecting…' : 'Reject offer'}
        </button>
      </div>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Reject offer"
      size="sm"
      describedBy={promptId}
      footer={footer}
    >
      <p id={promptId} className="reject-modal__prompt">
        Reject this offer? You can include a brief reason for the buyer (optional).
      </p>

      <fieldset className="reject-modal__fieldset">
        <legend className="reject-sr-only">Rejection reason</legend>
        <div className="reject-modal__field">
          <label htmlFor={reasonId} className="reject-modal__label">
            Reason (optional)
          </label>
          <textarea
            id={reasonId}
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON))}
            maxLength={MAX_REASON}
            placeholder="Share a short note for the buyer."
            disabled={submitting}
            aria-describedby={counterId}
          />
          <div
            id={counterId}
            className="reject-modal__counter"
            aria-live={showAnnounce ? 'polite' : 'off'}
          >
            {showAnnounce
              ? `${remaining} characters remaining`
              : `${reason.length} / ${MAX_REASON}`}
          </div>
        </div>
      </fieldset>
    </Modal>
  );
}
